import { useTranslate } from '@tolgee/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MainHeaderTitles,
  SubHeaderTitles,
  SubTitles
} from '../../components/brandOnboarding/headerCards/titles';
import { Button, Form, InputGroup } from 'react-bootstrap';

import webfonts from '../settings/branding/template_branding/webfonts.json';

import {
  saveBrandingInformation,
  selectALogo,
  setSubmittingInfo,
  updateBrandColors,
  updateBrandHomePage,
  updateCustomRectangleLogo,
  updateCustomSquareLogo,
  updateLogoBgColor,
  updateLogoWithType,
  updateSelectedFont
} from '../../redux/slices/brandOnboarding/brandOnboarding';
import {
  generateImageHash,
  handleImageUploadWithUrl,
  isSquareImage,
  replaceDomainInUrl,
  replaceUrl
} from '../../components/brandOnboarding/helpers/helperFunctions';
import {
  PLACEHOLDER,
  PRIMARY_COLOR,
  RECTANGLE_LOGO,
  SECONDARY_COLOR,
  SQUARE_LOGO
} from '../../components/brandOnboarding/constants';
import BrandImage from '../../components/brandOnboarding/cards/imageCards';
import SelectColor from '../../components/brandOnboarding/cards/colorSelect';
import { FontSelector, WebFont } from '../../components/generic/fontSelector';
import { FilePondUploader } from '../../components/generic/filePondUploader';
import { useParams } from 'react-router-dom';
import { getLocationDetails } from '../../redux/slices/location/locationDetails';
import toast from 'react-hot-toast';

function extractDomain(inputUrl) {
  try {
    if (inputUrl.indexOf('.') === -1) {
      return null; // Return null for invalid URLs
    }

    // Ensure input has a protocol, otherwise prepend 'http://'
    if (!inputUrl.startsWith('http://') && !inputUrl.startsWith('https://')) {
      inputUrl = 'http://' + inputUrl;
    }

    const url = new URL(inputUrl);

    console.log('hostname', url.hostname);

    return url.hostname; // Extracts domain name
  } catch (error) {
    return null; // Return null for invalid URLs
  }
}

function isImageEmptyOrUniform(imageSrc) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // Handle CORS issues if needed
    img.src = imageSrc;

    img.onload = function () {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the image onto the canvas
      ctx.drawImage(img, 0, 0, img.width, img.height);

      // Get pixel data
      const imageData = ctx.getImageData(0, 0, img.width, img.height);
      const pixels = imageData.data;

      let firstPixel = null;
      let sumR = 0,
        sumG = 0,
        sumB = 0;
      let sumR2 = 0,
        sumG2 = 0,
        sumB2 = 0;
      let pixelCount = 0;
      let isUniform = true;

      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const a = pixels[i + 3];

        if (a !== 0) {
          // Ignore fully transparent pixels
          if (!firstPixel) {
            firstPixel = `${r},${g},${b},${a}`;
          } else if (firstPixel !== `${r},${g},${b},${a}`) {
            isUniform = false;
          }

          sumR += r;
          sumG += g;
          sumB += b;
          sumR2 += r * r;
          sumG2 += g * g;
          sumB2 += b * b;
          pixelCount++;
        }
      }

      if (pixelCount === 0) {
        // Image is fully transparent
        resolve(true);
        return;
      }

      if (isUniform) {
        // All visible pixels have the same color
        resolve(true);
        return;
      }

      // Compute color variance (standard deviation)
      const meanR = sumR / pixelCount;
      const meanG = sumG / pixelCount;
      const meanB = sumB / pixelCount;

      const varianceR = sumR2 / pixelCount - meanR * meanR;
      const varianceG = sumG2 / pixelCount - meanG * meanG;
      const varianceB = sumB2 / pixelCount - meanB * meanB;

      const stdDevR = Math.sqrt(varianceR);
      const stdDevG = Math.sqrt(varianceG);
      const stdDevB = Math.sqrt(varianceB);

      // Define a threshold for variance to determine if the image is "blended"
      const threshold = 5; // Adjust as needed
      if (stdDevR < threshold && stdDevG < threshold && stdDevB < threshold) {
        resolve(true); // Image is too uniform (blended)
      } else {
        resolve(false); // Image has content
      }
    };

    img.onerror = function () {
      console.error('Error loading image.');
      reject(new Error('Error loading image.'));
    };
  });
}

export interface brandOnboardingRootState {
  brandOnboarding: {
    ui: any;
    lomavisCreatorConfig: any;
    externalContentConfig: any;
    brandHomePage: string;
    logoLoadUrls: string[];
    loadedLogos: string[];
    squareLogos: string[];
    rectangleLogos: string[];
    selectedLogos: {
      square: string | any;
      rectangle: string | any;
    };
    logoBackGroundColor: string;
    brandColors: {
      primaryColor: string;
      secondaryColor: string;
    };
    selectedFontFamily: WebFont;
    customSquareLogo: any;
    customRectangleLogo: any;
    submittingInfo: boolean;
    onError: any;
  };
}

const BrandOnboarding: React.FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { locationslug } = useParams();
  useEffect(() => {
    dispatch(getLocationDetails(locationslug) as any);
  }, [dispatch, locationslug]);

  const locationDetails = useSelector((state: any) => state.locationDetails);

  useEffect(() => {
    // adjust root classes to fill up 100% of the screen. Other pages could be affected by a global change,
    // so we only do it here
    const root = document.getElementById('root');
    root.classList.add('h-100', 'd-flex', 'flex-column');
  });

  const [brandUrl, setBrandUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [brandInfoPage, setBrandInfoPage] = useState(false);

  const {
    logoLoadUrls,
    squareLogos,
    rectangleLogos,
    selectedLogos,
    logoBackGroundColor,
    brandColors,
    selectedFontFamily,
    customSquareLogo,
    customRectangleLogo,
    submittingInfo,
    onError
  } = useSelector((state: brandOnboardingRootState) => state.brandOnboarding);

  const handleSaveBrandHome = async () => {
    if (!extractDomain(brandUrl)) {
      toast.error(
        t('lomavis.settings.branding.template_branding.invalid_url_error'),
        { position: 'top-right', id: 'invalid_url_error' }
      );
      return;
    }

    return new Promise(async (resolve) => {
      dispatch(updateBrandHomePage({ url: brandUrl }));
      setIsLoading(true);

      try {
        for (const url of logoLoadUrls) {
          let newUrl = replaceDomainInUrl(url, PLACEHOLDER, brandUrl);

          let genericResultUrl = replaceDomainInUrl(
            url,
            PLACEHOLDER,
            'linkedi.com'
          );

          const cldNewUrl = replaceUrl(newUrl);
          const cldGenericUrl = replaceUrl(genericResultUrl);

          const brandGenericHash = await generateImageHash(cldGenericUrl);
          const newUrlHash = await generateImageHash(cldNewUrl);

          // const newImageHash = await generateImageHash(imgCldUrl.toURL());
          if (
            brandGenericHash === newUrlHash &&
            newUrl.indexOf('redirect-to-external-ima') < 0
          ) {
            continue;
          }

          // const isEmpty = await isImageEmptyOrUniform(newUrl);

          // if (isEmpty) {
          //   console.log('Empty:', newUrl);
          //    continue;
          // }

          const isSquare = await isSquareImage(newUrl);

          if (isSquare) {
            dispatch(
              updateLogoWithType({
                url: newUrl,
                type: SQUARE_LOGO
              })
            );
          } else {
            dispatch(
              updateLogoWithType({
                url: newUrl,
                type: RECTANGLE_LOGO
              })
            );
          }
          console.log('new url', newUrl);
        }

        console.log('Saved:', brandUrl);
      } catch (e) {
        console.log('FAILED', e);
      } finally {
        console.log('type logos', rectangleLogos, squareLogos);

        setIsLoading(false);
        setBrandInfoPage(true);
      }

      setBrandInfoPage(true);
      resolve(0);
    });
  };
  //   useEffect(() => {
  //     if (rectangleLogos.length > 0 && !selectedLogos.rectangle) {
  //       dispatch(
  //         selectALogo({
  //           url: rectangleLogos[0],
  //           type: RECTANGLE_LOGO
  //         })
  //       );
  //     }
  //     if (squareLogos.length > 0 && !selectedLogos.square) {
  //       dispatch(
  //         selectALogo({
  //           url: squareLogos[0],
  //           type: SQUARE_LOGO
  //         })
  //       );
  //     }
  //   }, [squareLogos, rectangleLogos]);

  const handleSquareFilesUpdate = useCallback(
    (file) => {
      dispatch(updateCustomSquareLogo({ logoObj: file }));
    },
    [dispatch]
  );
  const handleRectangleFilesUpdate = useCallback(
    (file) => {
      dispatch(updateCustomRectangleLogo({ logoObj: file }));
    },
    [dispatch]
  );

  const handleSaveBrandInfo = async () => {
    let data = {
      primary: brandColors.primaryColor ? brandColors.primaryColor : '#ffffff',
      secondary: brandColors.secondaryColor
        ? brandColors.secondaryColor
        : '#ffffff',
      fontFamily: selectedFontFamily?.family
    };

    if (!data.primary || !data.secondary || !data.fontFamily) {
      toast.error(
        t('lomavis.settings.branding.template_branding.data_missing_error'),
        { position: 'top-right', id: 'data_missing_error' }
      );
      return;
    }

    const squareLogoUuid = customSquareLogo
      ? customSquareLogo?.uploadPreset?.uuid
      : await handleImageUploadWithUrl(
          selectedLogos.square,
          locationDetails.uuid,
          null,
          () => {
            toast.error(
              t('lomavisTemplate.onBoarding.imageUploadError_error'),
              { position: 'top-right' }
            );
          }
        );

    const rectangleLogoUuid = customRectangleLogo
      ? customRectangleLogo?.uploadPreset?.uuid
      : await handleImageUploadWithUrl(
          selectedLogos.rectangle,
          locationDetails.uuid,
          null,
          () => {
            toast.error(
              t('lomavisTemplate.onBoarding.imageUploadError_error'),
              { position: 'top-right' }
            );
          }
        );

    // @ts-ignore
    data = {
      primary: brandColors.primaryColor ? brandColors.primaryColor : '#ffffff',
      secondary: brandColors.secondaryColor
        ? brandColors.secondaryColor
        : '#ffffff',
      fontFamily: selectedFontFamily?.family,
      // @ts-ignore
      squareLogo: squareLogoUuid,
      rectangleLogo: rectangleLogoUuid,
      uuid: locationDetails?.uuid
    };

    if (!data.primary || !data.secondary || !data.fontFamily) {
      toast.error(
        t('lomavis.settings.branding.template_branding.data_missing_error'),
        { position: 'top-right', id: 'data_missing_error' }
      );
      return;
    }

    dispatch(setSubmittingInfo({ status: true }));
    dispatch(
      saveBrandingInformation(
        data,
        () => {
          toast.success(
            t('lomavis.settings.branding.template_branding.save_success'),
            { position: 'top-right' }
          );

          // @ts-ignore
          window.location = window.location.pathname;
        },
        () =>
          toast.error(
            t('lomavis.settings.branding.template_branding.save_error'),
            { position: 'top-right' }
          )
      ) as any
    );
  };

  return (
    <div className={'d-flex justify-content-center h-100'}>
      {' '}
      {!brandInfoPage ? (
        <div
          style={{ maxWidth: '900px', width: '100%' }}
          className="mx-5 h-100 bg-white d-flex flex-column align-items-center justify-content-center"
        >
          <div className="w-100 d-flex flex-column gap-3 justify-content-center align-items-center ">
            <MainHeaderTitles
              align="center"
              text={t('lomavis.Creator.brandOnboading.setup')}
            />
            <SubTitles
              align="center"
              text={t('lomavis.Creator.brandOnboading.setupSub')}
            />

            <Form.Control
              style={{ maxWidth: '400px', width: '100%' }}
              placeholder={t('lomavisCreator.brandOnboading.homepage_input')}
              aria-label="homepage_input"
              aria-describedby="basic-addon1"
              id="homepage_input"
              value={brandUrl}
              onChange={(e) => {
                setBrandUrl(e.target.value);
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  await handleSaveBrandHome();
                }
              }}
              disabled={isLoading}
              autoComplete="off"
            />
            <div>
              <Button
                variant="primary"
                onClick={handleSaveBrandHome}
                className="mt-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t('lomavisCreator.brandOnboading.loading')}
                  </>
                ) : (
                  t('lomavisCreator.brandOnboading.save')
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{ maxWidth: '900px', width: '100%' }}
          className="h-100 bg-white d-flex flex-column justify-content-start rounded"
        >
          <div className="w-100 d-flex p-4 justify-content-center ">
            <div className="w-100 w-md-75 d-flex flex-column gap-5 p-5">
              <div style={{}} className="w-100 d-flex flex-column gap-4">
                <div className="d-flex flex-column gap-1">
                  <MainHeaderTitles
                    text={t('lomavisCreator.brandOnboading.Logos')}
                    align="start"
                  />
                  <p className="text-muted">
                    {t('lomavisCreator.brandOnboading.Logos_text')}
                  </p>
                </div>

                <div className="d-flex flex-column gap-1">
                  <SubHeaderTitles
                    text={t('lomavisTemplate.brandOnboading.Square')}
                    align="start"
                  />
                  <div className="w-100 d-flex flex-column gap-3">
                    <div className="w-100 d-flex gap-4">
                      {!customSquareLogo &&
                        squareLogos?.map((item) => (
                          <BrandImage
                            isSelected={item === selectedLogos.square}
                            onSelect={() => {
                              dispatch(
                                selectALogo({ url: item, type: SQUARE_LOGO })
                              );
                            }}
                            imageUrl={item}
                            logoType={SQUARE_LOGO}
                          />
                        ))}
                    </div>

                    <div className="w-100 bg-light-secondary p-2 rounded">
                      <FilePondUploader
                        defaultFiles={[]}
                        onFilesUpdate={handleSquareFilesUpdate}
                        allowMultiple={false}
                        shape="square"
                        name={t('lomavisTemplate.filepond_square_file')}
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column gap-1">
                  <SubHeaderTitles
                    text={t('lomavisTemplate.brandOnboading.Rectangle')}
                    align="start"
                  />
                  <div className="w-100 d-flex flex-column gap-3">
                    <div className="w-100 d-flex gap-4">
                      {!customRectangleLogo &&
                        rectangleLogos?.map((item) => (
                          <BrandImage
                            isSelected={item === selectedLogos.rectangle}
                            onSelect={() => {
                              dispatch(
                                selectALogo({
                                  url: item,
                                  type: RECTANGLE_LOGO
                                })
                              );
                            }}
                            imageUrl={item}
                            aspectRatio={4 / 3}
                            logoType={RECTANGLE_LOGO}
                          />
                        ))}
                    </div>

                    <div className="w-100 bg-light-secondary p-2 rounded">
                      <FilePondUploader
                        defaultFiles={[]}
                        onFilesUpdate={handleRectangleFilesUpdate}
                        allowMultiple={false}
                        shape="rectangle"
                        name={t('lomavisTemplate.filepond_rectangle_file')}
                      />
                    </div>
                  </div>
                </div>

                {/*<div className="d-flex flex-column gap-1">*/}
                {/*  <SubHeaderTitles*/}
                {/*    text={t('lomavisTemplate.brandOnboading.bgColor')}*/}
                {/*    align="start"*/}
                {/*  />*/}
                {/*  <p className="text-muted">*/}
                {/*    {t('lomavisTemplate.brandOnboarding.bgColor_text')}*/}
                {/*  </p>*/}

                {/*  <div className="w-100 d-flex flex-column gap-3">*/}
                {/*    <InputGroup className="mb-3 w-100 d-flex gap-2">*/}
                {/*      <div*/}
                {/*        style={{*/}
                {/*          aspectRatio: 1,*/}
                {/*          height: '100%',*/}
                {/*          backgroundColor: logoBackGroundColor*/}
                {/*            ? logoBackGroundColor*/}
                {/*            : '#ffffff'*/}
                {/*        }}*/}
                {/*        className="border rounded-3"*/}
                {/*      ></div>*/}
                {/*      <Form.Control*/}
                {/*        placeholder="e.g #ffffff"*/}
                {/*        aria-label="Background color"*/}
                {/*        aria-describedby="basic-addon1"*/}
                {/*        id="bgColor"*/}
                {/*        value={logoBackGroundColor}*/}
                {/*        onChange={(e) => {*/}
                {/*          dispatch(*/}
                {/*            updateLogoBgColor({ color: e.target.value })*/}
                {/*          );*/}
                {/*        }}*/}
                {/*      />*/}
                {/*    </InputGroup>*/}
                {/*  </div>*/}
                {/*</div>*/}
              </div>
              <div style={{}} className="w-100 d-flex flex-column gap-4">
                <MainHeaderTitles
                  align="start"
                  text={t('lomavisTemplate.brandOnboarding.brandColors')}
                />
                <p className="text-muted">
                  {t('lomavisTemplate.brandOnboarding.brandColors_text')}
                </p>
                <div className="w-100 d-flex flex-column gap-3">
                  <SelectColor
                    value={brandColors.primaryColor}
                    handleChange={(e) => {
                      dispatch(
                        updateBrandColors({
                          color: e.target.value,
                          type: PRIMARY_COLOR
                        })
                      );
                    }}
                    label={t(
                      'lomavisTemplate.brandOnboarding.brand_primaryColor'
                    )}
                    imgSrc={
                      customRectangleLogo?.previewImageUrl ||
                      customSquareLogo?.previewImageUrl ||
                      selectedLogos?.rectangle ||
                      selectedLogos?.square ||
                      ''
                    }
                    selectAColor={(color) => {
                      dispatch(
                        updateBrandColors({
                          color: color,
                          type: PRIMARY_COLOR
                        })
                      );
                    }}
                    primaryColor={true}
                  />

                  <SelectColor
                    value={brandColors.secondaryColor}
                    handleChange={(e) => {
                      dispatch(
                        updateBrandColors({
                          color: e.target.value,
                          type: SECONDARY_COLOR
                        })
                      );
                    }}
                    label={t(
                      'lomavisTemplate.brandOnboarding.brand_secondaryColor'
                    )}
                    imgSrc={
                      customRectangleLogo?.previewImageUrl ||
                      customSquareLogo?.previewImageUrl ||
                      selectedLogos?.rectangle ||
                      selectedLogos?.square ||
                      ''
                    }
                    selectAColor={(color) => {
                      dispatch(
                        updateBrandColors({
                          color: color,
                          type: SECONDARY_COLOR
                        })
                      );
                    }}
                    primaryColor={false}
                  />
                </div>
              </div>

              <div style={{}} className="w-100 d-flex flex-column gap-4">
                <MainHeaderTitles
                  align="start"
                  text={t('lomavisTemplate.brandOnboarding.font')}
                />
                <p className="text-muted">
                  {t('lomavisTemplate.brandOnboarding.font_text')}
                </p>
                <div>
                  <FontSelector
                    selectedFont={selectedFontFamily}
                    onFontChange={(newFont) => {
                      console.log(newFont);
                      dispatch(updateSelectedFont({ font: newFont }));
                    }}
                    options={webfonts.items}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="w-100 d-flex justify-content-end">
            <Button
              variant="primary"
              onClick={handleSaveBrandInfo}
              className="m-5"
              disabled={submittingInfo}
            >
              {submittingInfo ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {t('lomavisCreator.brandOnboading.loading')}
                </>
              ) : (
                t('lomavisCreator.brandOnboading.continue')
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandOnboarding;
